<script setup>
import { computed } from 'vue';

import HrIcon from '../primitives/HrIcon.vue';
import HrItem from '../primitives/HrItem.vue';
import HrSelect from '../primitives/HrSelect.vue';

/**
 * Single-selection field whose options are icons, picked from a grid.
 *
 * Built on the same select as HrListSelect, so the field, keyboard navigation and listbox
 * semantics are shared; only the options are laid out as a grid of icon cells instead of rows.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Choosing an icon that identifies something the user creates, such as a status or a type
 * @avoidWhen The options have names worth reading — use HrListSelect with a leading icon per row
 * @related HrListSelect
 * @a11y A listbox like any select: arrow keys move through the icons, Enter picks one. Each icon is
 *   announced by its name, so prefer self-explanatory icon names.
 */
defineOptions({ name: 'HrIconSelect' });

const props = defineProps({
  /** Name of the selected icon. Supports v-model. */
  modelValue: {
    type: String,
    default: null,
  },
  /**
   * Icon names to choose from.
   * @example ["group", "phone", "school", "home", "flag"]
   */
  options: {
    type: Array,
    required: true,
  },
  /** Field label. Required, since an icon cannot name the field it sits in. */
  label: {
    type: String,
    required: true,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when an icon is picked; payload is its name. */
  'update:modelValue': null,
});

/*
 * Icon names are the only thing a screen reader has to announce. Material Symbols prefixes
 * (`sym_o_`, `o_`, …) say which font the icon comes from, not what it shows.
 */
function spokenName(icon) {
  return icon.replace(/^(sym_[a-z]_|[a-z]_)/, '').replaceAll('_', ' ');
}

/*
 * The select renders its options through a virtual scroller that only builds the rows near the
 * viewport, measuring them as list rows. A grid breaks that measurement, and an icon set is small,
 * so every option is rendered up front.
 */
const virtualScrollSliceSize = computed(() => Math.max(props.options.length, 1));
</script>

<template>
  <div class="hr-icon-select">
    <span
      class="hr-icon-select__label-sizer hr-body-large"
      aria-hidden="true"
    >{{ label }}</span>
    <hr-select
      :model-value="modelValue"
      :options="options"
      :label="label"
      :disable="disable"
      :virtual-scroll-slice-size="virtualScrollSliceSize"
      popup-content-class="hr-select__popup hr-icon-select__popup"
      hide-bottom-space
      class="hr-icon-select__field"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #selected-item="{ opt }">
        <hr-icon
          :name="opt"
          role="img"
          aria-hidden="false"
          :aria-label="spokenName(opt)"
        />
      </template>
      <template #option="{ opt, itemProps }">
        <hr-item
          v-bind="itemProps"
          :aria-label="spokenName(opt)"
          class="hr-icon-select__option"
        >
          <hr-icon :name="opt" />
        </hr-item>
      </template>
    </hr-select>
  </div>
</template>

<style lang="scss" scoped>
/*
 * The field's label is absolutely positioned, so it adds nothing to the width and a field sized to
 * a single icon cuts it off. An invisible copy in the same grid cell sizes the field to fit the
 * label in any language. Its padding mirrors the outlined control: 12px before the text, and the
 * 12px control padding plus the 24px dropdown arrow and its 12px gap after it.
 */
.hr-icon-select {
  display: inline-grid;
}

.hr-icon-select__label-sizer,
.hr-icon-select__field {
  grid-area: 1 / 1;
}

.hr-icon-select__label-sizer {
  visibility: hidden;
  height: 0;
  padding-inline: 12px 48px;
  white-space: nowrap;
}

.hr-icon-select__option {
  min-height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
}
</style>

<style lang="scss">
/*
 * Not scoped: the select builds the options wrapper itself and teleports it to the body. Unguarded
 * by [data-theme], since the grid is the component's layout rather than its theme.
 */
.hr-icon-select__popup .q-virtual-scroll__content {
  display: grid;
  grid-template-columns: repeat(5, 40px);
  gap: 4px;
  padding: 8px;
}
</style>
